// src/features/services/sales/hooks/useStock.ts
// ─────────────────────────────────────────────────────────────────────────────
// Stock — FE hook (hook-owns-all-state pattern).
//
// Owns: fetch, cache, mutations, derived state, error handling.
// Components are pure renderers.
// ─────────────────────────────────────────────────────────────────────────────
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchStockListApi,
  fetchStockBalanceApi,
  adjustStockApi,
  fetchLowStockAlertsApi,
  reserveStockApi,
  commitReservationApi,
  releaseReservationApi,
  fetchReservationsApi,
  fetchLotsApi,
  fetchAvailabilityApi,
  STOCK_KEYS,
  type StockListResponse,
  type StockBalance,
  type AdjustStockPayload,
  type AdjustStockResponse,
  type LowStockResponse,
  type ReserveStockPayload,
  type ReserveStockResponse,
  type ReservationListResponse,
  type LotListResponse,
  type StockAvailability,
} from '../api/stockApi';

/**
 * Full stock hook — list balances + adjust mutation.
 */
export function useStock(params?: { limit?: number }) {
  const qc = useQueryClient();

  const list = useQuery<StockListResponse, Error>({
    queryKey: STOCK_KEYS.list(params),
    queryFn: () => fetchStockListApi(params),
    staleTime: 30_000,
  });

  const adjust = useMutation<AdjustStockResponse, Error, { sku: string; payload: AdjustStockPayload }>({
    mutationFn: ({ sku, payload }) => adjustStockApi(sku, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: STOCK_KEYS.all }),
  });

  /** Map of sku → quantity for quick lookups. */
  const balanceMap: Record<string, number> = {};
  for (const b of list.data?.items ?? []) {
    if (b.sku) balanceMap[b.sku] = b.quantity;
  }

  return { ...list, adjust, balanceMap };
}

/**
 * Single SKU stock balance hook.
 */
export function useStockBalance(sku: string | null) {
  return useQuery<StockBalance, Error>({
    queryKey: STOCK_KEYS.detail(sku!),
    queryFn: () => fetchStockBalanceApi(sku!),
    enabled: !!sku,
    staleTime: 30_000,
  });
}

// ── P3.1 / P3.2 — Low-stock alerts ──────────────────────────────────────────

/**
 * Hook: all SKUs below their reorder point.
 */
export function useLowStockAlerts() {
  return useQuery<LowStockResponse, Error>({
    queryKey: STOCK_KEYS.lowStock,
    queryFn: fetchLowStockAlertsApi,
    staleTime: 60_000,
  });
}

// ── P3.3 — Reservations ─────────────────────────────────────────────────────

/**
 * Hook: reserve, commit, release stock.
 */
export function useStockReservations(sku: string | null) {
  const qc = useQueryClient();

  const list = useQuery<ReservationListResponse, Error>({
    queryKey: STOCK_KEYS.reservations(sku!),
    queryFn: () => fetchReservationsApi(sku!),
    enabled: !!sku,
    staleTime: 30_000,
  });

  const reserve = useMutation<ReserveStockResponse, Error, ReserveStockPayload>({
    mutationFn: (payload) => reserveStockApi(sku!, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STOCK_KEYS.reservations(sku!) });
      qc.invalidateQueries({ queryKey: STOCK_KEYS.detail(sku!) });
      qc.invalidateQueries({ queryKey: STOCK_KEYS.lowStock });
    },
  });

  const commit = useMutation<void, Error, string>({
    mutationFn: commitReservationApi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STOCK_KEYS.all });
    },
  });

  const release = useMutation<void, Error, string>({
    mutationFn: releaseReservationApi,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STOCK_KEYS.reservations(sku!) });
      qc.invalidateQueries({ queryKey: STOCK_KEYS.lowStock });
    },
  });

  return { ...list, reserve, commit, release };
}

// ── P3.3 — Availability ─────────────────────────────────────────────────────

/**
 * Hook: available quantity (on_hand minus reserved) for a single SKU.
 */
export function useStockAvailability(sku: string | null) {
  return useQuery<StockAvailability, Error>({
    queryKey: STOCK_KEYS.availability(sku!),
    queryFn: () => fetchAvailabilityApi(sku!),
    enabled: !!sku,
    staleTime: 30_000,
  });
}

// ── P3.4 — Lot/Batch tracking ───────────────────────────────────────────────

/**
 * Hook: lot list (FEFO-sorted) for a single SKU.
 */
export function useStockLots(sku: string | null) {
  return useQuery<LotListResponse, Error>({
    queryKey: STOCK_KEYS.lots(sku!),
    queryFn: () => fetchLotsApi(sku!),
    enabled: !!sku,
    staleTime: 30_000,
  });
}
